import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Clock, Truck, Users, Navigation, ExternalLink } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { de } from 'date-fns/locale';
import { fetchPublicTour } from '../services/tourLinkService';
import { loadGoogleMaps } from '../utils/loadGoogleMaps';

const OPERATION_LABELS = { beladung: '↑ Beladung', entladung: '↓ Entladung', neutral: '↕ Be- & Entladung' };

const formatTourDate = (value) => {
  if (!value) return '';
  const date = parseISO(value);
  return isValid(date) ? format(date, 'EEEE, dd.MM.yyyy', { locale: de }) : value;
};

/** Öffentliche, schreibgeschützte Ansicht einer geplanten Tour (/tour/:token). */
const TourReviewPage = ({ token }) => {
  const [tour, setTour] = useState(null);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);

  useEffect(() => {
    fetchPublicTour(token).then(setTour).catch(err => setError(err.message));
  }, [token]);

  useEffect(() => {
    if (!tour || !mapRef.current) return;
    let cancelled = false;
    loadGoogleMaps(['geometry']).then(maps => {
      if (cancelled) return;
      const map = new maps.Map(mapRef.current, { zoom: 9, center: { lat: 48.9, lng: 8.5 }, mapTypeControl: false, streetViewControl: false });
      const bounds = new maps.LatLngBounds();
      if (tour.overviewPolyline && maps.geometry?.encoding) {
        const path = maps.geometry.encoding.decodePath(tour.overviewPolyline);
        new maps.Polyline({ path, map, strokeColor: '#2563eb', strokeWeight: 5, strokeOpacity: 0.85 });
        path.forEach(p => bounds.extend(p));
      }
      (tour.stations || []).forEach((station, index) => {
        if (!station.location) return;
        new maps.Marker({ position: station.location, map, label: { text: String(index + 1), color: '#fff', fontWeight: '600' }, title: station.address });
        bounds.extend(station.location);
      });
      if (!bounds.isEmpty()) map.fitBounds(bounds);
    }).catch(err => console.warn(err));
    return () => { cancelled = true; };
  }, [tour]);

  if (error) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 text-gray-700">{error}</div>;
  }
  if (!tour) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const stations = tour.stations || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">Geplante Tour</p>
          <h1 className="text-xl font-bold text-gray-900">{tour.tourName || tour.tourId}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mt-1">
            <span>{formatTourDate(tour.tourDate)}</span>
            {tour.tourStartTime && <span className="inline-flex items-center gap-1"><Clock size={14} />Start {tour.tourStartTime}</span>}
            {tour.totalDuration ? <span>{tour.totalDuration} Min. gesamt</span> : null}
            {tour.totalDistanceKm ? <span className="inline-flex items-center gap-1"><Navigation size={14} />{String(tour.totalDistanceKm).replace('.', ',')} km</span> : null}
            {tour.terminart && <span>{tour.terminart}</span>}
          </div>
          {(tour.employees?.length > 0 || tour.vehicles?.length > 0) && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700 mt-2">
              {tour.vehicles?.length > 0 && <span className="inline-flex items-center gap-1"><Truck size={14} />{tour.vehicles.join(', ')}</span>}
              {tour.employees?.length > 0 && <span className="inline-flex items-center gap-1"><Users size={14} />{tour.employees.join(', ')}</span>}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 grid grid-cols-1 lg:grid-cols-5 gap-4">
        <section className="lg:col-span-2 space-y-2" aria-label="Stopps">
          {stations.map((station, index) => (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-3 flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-semibold flex items-center justify-center">{index + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="flex justify-between gap-2">
                  <span className="text-sm font-medium text-gray-900">{station.address}</span>
                  {station.arrival && <span className="text-sm tabular-nums text-gray-700 whitespace-nowrap">{station.arrival}{station.departure ? `–${station.departure}` : ''}</span>}
                </div>
                {station.dealTitles?.length > 0 && <div className="text-xs text-gray-600 mt-0.5">{station.dealTitles.join(', ')}</div>}
                <div className="text-xs text-gray-500 mt-0.5">
                  {[OPERATION_LABELS[station.operationType], station.durationText && `Fahrt ${station.durationText}`, station.distanceText].filter(Boolean).join(' · ')}
                </div>
              </div>
            </div>
          ))}
          {tour.googleMapsUrl && (
            <a href={tour.googleMapsUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              <MapPin size={15} /> In Google Maps öffnen <ExternalLink size={13} />
            </a>
          )}
          {tour.updatedAt && (
            <p className="text-xs text-gray-400">Stand: {format(new Date(tour.updatedAt), 'dd.MM.yyyy HH:mm')}</p>
          )}
        </section>
        <section className="lg:col-span-3" aria-label="Karte">
          <div ref={mapRef} className="w-full h-[420px] lg:h-[calc(100vh-10rem)] rounded-lg bg-gray-200" />
        </section>
      </main>
    </div>
  );
};

export default TourReviewPage;
